import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Copy, Check, ChevronDown, ChevronUp, Loader2, Mail, MessageCircle, Send,
  TrendingUp, DollarSign, Gift, Calendar, Star, Award
} from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { SiFacebook, SiX, SiMessenger, SiWhatsapp } from "react-icons/si";
import { format } from "date-fns";
import type { Referral } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";

const REFERRAL_EMAIL_SUBJECT = "What I've been using to run my rentals";
const REFERRAL_EMAIL_BODY = `I used to spend way too much time juggling tools — one app for messaging members, a spreadsheet for underwriting the next deal, another tab for pricing, and the PadSplit dashboard open on top of it all. Something was always falling through the cracks.

Since I started using PadPulse, everything lives in one place. Property management, underwriting, dynamic pricing, automated messaging, maintenance tracking — even AI-generated listing photos — all built specifically for how PadSplit hosts actually operate. I'm spending way less time switching between tabs and way more time actually growing my portfolio.

If you're running PadSplits and feel like you're duct-taping five different tools together, give it a look. Here's my referral link:`;

type EarningsDashboard = {
  tier: "founder_partner" | "regular_host";
  referralCode: string | null;
  referrals: Referral[];
  accruals: Array<{
    id: number;
    referralId: number;
    periodMonth: string;
    commissionCents: number;
    statementId: number | null;
    createdAt: string;
  }>;
  statements: Array<{
    id: number;
    periodQuarter: string;
    totalCents: number;
    status: string;
    paidAt: string | null;
    createdAt: string;
  }>;
  credits: Array<{
    id: number;
    creditType: string;
    amountCents: number;
    expiresAt: string;
    redeemedAt: string | null;
    createdAt: string;
  }>;
  currentQuarter: string;
  currentQTotalCents: number;
  activeCreditCents: number;
};

export default function AppRefer() {
  const queryClient = useQueryClient();
  usePageTitle("Refer & Earn");
  const { toast } = useToast();
  const [emailInput, setEmailInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  const codeQuery = useQuery<{ referralCode: string }>({
    queryKey: ["/api/app/referral-code"],
  });

  const earningsQuery = useQuery<EarningsDashboard>({
    queryKey: ["/api/app/refer-host/earnings"],
  });

  const inviteMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const res = await apiRequest("POST", "/api/app/referrals/invite", { emails });
      return res.json();
    },
    onSuccess: (data: { count: number }) => {
      toast({ title: `${data.count} invitation${data.count > 1 ? "s" : ""} sent!` });
      setEmailInput("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/refer-host/earnings"] });
    },
    onError: () => {
      toast({ title: "Failed to send invitations", variant: "destructive" });
    },
  });

  const referralCode = codeQuery.data?.referralCode || earningsQuery.data?.referralCode || "";
  const referralLink = referralCode ? `https://padpulse.io/?ref=${referralCode}` : "";
  const earnings = earningsQuery.data;
  const tier = earnings?.tier;

  function handleCopy() {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({ title: "Link copied to clipboard!" });
    setTimeout(() => setCopied(false), 2000);
  }

  function handleSendInvites() {
    const emails = emailInput.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.length > 0);
    if (emails.length === 0) {
      toast({ title: "Please enter at least one email", variant: "destructive" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = emails.filter(e => !emailRegex.test(e));
    if (invalid.length > 0) {
      toast({ title: "Invalid email", description: `These don't look right: ${invalid.join(", ")}`, variant: "destructive" });
      return;
    }
    inviteMutation.mutate(emails);
  }

  function shareUrl(platform: string) {
    if (!referralLink) return;
    const text = "Check out PadPulse — the all-in-one tool for PadSplit hosts.";
    const urls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      sms: `sms:?body=${encodeURIComponent(text + " " + referralLink)}`,
      email: `mailto:?subject=${encodeURIComponent(REFERRAL_EMAIL_SUBJECT)}&body=${encodeURIComponent(REFERRAL_EMAIL_BODY + "\n\n" + referralLink)}`,
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`,
      messenger: `https://www.facebook.com/dialog/send?link=${encodeURIComponent(referralLink)}&app_id=0&redirect_uri=${encodeURIComponent(referralLink)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + referralLink)}`,
    };
    window.open(urls[platform], "_blank");
  }

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-800",
    signed_up: "bg-blue-100 text-blue-800",
    active: "bg-green-100 text-green-800",
  };

  const statusLabels: Record<string, string> = {
    pending: "Pending",
    signed_up: "Signed Up",
    active: "Active",
  };

  const statementStatusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-600",
    sent: "bg-blue-100 text-blue-700",
    paid: "bg-green-100 text-green-700",
    voided: "bg-red-100 text-red-700",
  };

  function formatCents(cents: number) {
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  }

  const referralList = earnings?.referrals || [];

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-refer-title" data-tour="refer-heading">Share PadPulse with hosts who need it</h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="refer" />
            <GuideButton pageKey="refer" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="refer" />
        <p className="text-gray-500 mb-6">Help fellow PadSplit hosts discover a better way to manage their properties.</p>

        {tier && (
          <div className={`rounded-lg p-4 mb-8 border ${tier === "founder_partner" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
            <div className="flex items-start gap-3">
              {tier === "founder_partner"
                ? <Award className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                : <Gift className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              }
              <div>
                {tier === "founder_partner" ? (
                  <>
                    <p className="font-semibold text-amber-800 text-sm">Founder / Partner tier — 20% commission for 12 months</p>
                    <p className="text-xs text-amber-700 mt-1">You earn 20% of each referred user's Growth or Professional subscription for up to 12 months after they sign up. Commission only accrues for months they are actually paying.</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-blue-800 text-sm">Host tier — $25 credit per paying referral</p>
                    <p className="text-xs text-blue-700 mt-1">You receive a $25 bill credit when your referral becomes a paying Growth or Professional member. They also get a $25 credit on their first bill.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Share your unique referral link</h3>
            {codeQuery.isLoading ? (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating your link...
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Input
                    value={referralLink}
                    readOnly
                    className="flex-1 bg-gray-50 font-mono text-sm"
                    data-testid="input-referral-link"
                  />
                  <Button onClick={handleCopy} variant="outline" className="gap-2 shrink-0" data-testid="button-copy-link" data-tour="refer-link">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copied!" : "Copy link"}
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={() => shareUrl("facebook")} className="h-10 w-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-facebook">
                    <SiFacebook className="h-5 w-5" />
                  </button>
                  <button onClick={() => shareUrl("sms")} className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-sms">
                    <MessageCircle className="h-5 w-5" />
                  </button>
                  <button onClick={() => shareUrl("email")} className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-email">
                    <Mail className="h-5 w-5" />
                  </button>
                  <button onClick={() => shareUrl("x")} className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-x">
                    <SiX className="h-4 w-4" />
                  </button>
                  <button onClick={() => shareUrl("messenger")} className="h-10 w-10 rounded-full bg-[#0084FF] flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-messenger">
                    <SiMessenger className="h-5 w-5" />
                  </button>
                  <button onClick={() => shareUrl("whatsapp")} className="h-10 w-10 rounded-full bg-[#25D366] flex items-center justify-center text-white hover:opacity-90 transition-opacity" data-testid="button-share-whatsapp">
                    <SiWhatsapp className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Invite by email</h3>
            <p className="text-sm text-gray-500 mb-3">Type or paste your friends' email addresses and press Send invites</p>
            <div className="flex items-center gap-2 mb-3">
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                className="flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") handleSendInvites(); }}
                data-testid="input-invite-emails"
              />
              <Button
                onClick={handleSendInvites}
                disabled={inviteMutation.isPending}
                className="gap-2 bg-brand hover:bg-brand-hover shrink-0"
                data-testid="button-send-invites"
              >
                {inviteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send invites
              </Button>
            </div>

            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1.5 text-sm text-brand hover:underline"
              data-testid="button-preview-email"
            >
              {showPreview ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Preview email
            </button>

            {showPreview && (
              <div className="mt-3 border rounded-lg p-4 bg-gray-50">
                <div className="mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase">Subject:</span>
                  <p className="text-sm text-gray-900 font-medium">{REFERRAL_EMAIL_SUBJECT}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Message:</span>
                  <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">
                    {REFERRAL_EMAIL_BODY}
                    {referralLink && (
                      <span className="block mt-2 text-brand font-medium">{referralLink}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {earningsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading earnings...
            </div>
          ) : earnings && (
            <>
              {tier === "founder_partner" && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Earnings overview</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <p className="text-xs text-gray-500">This quarter ({earnings.currentQuarter})</p>
                        </div>
                        <p className="text-2xl font-bold text-green-600" data-testid="text-current-quarter-total">
                          {formatCents(earnings.currentQTotalCents)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="h-4 w-4 text-blue-600" />
                          <p className="text-xs text-gray-500">Total earned (all time)</p>
                        </div>
                        <p className="text-2xl font-bold text-blue-600" data-testid="text-total-earned">
                          {formatCents(earnings.accruals.reduce((s, a) => s + a.commissionCents, 0))}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {earnings.accruals.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Monthly accruals</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm" data-testid="table-accruals" data-tour="refer-status">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left px-4 py-2 font-medium text-gray-500">Month</th>
                              <th className="text-left px-4 py-2 font-medium text-gray-500">Commission</th>
                              <th className="text-left px-4 py-2 font-medium text-gray-500">Statement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {earnings.accruals.slice(0, 12).map((a) => (
                              <tr key={a.id} className="border-b last:border-0" data-testid={`row-accrual-${a.id}`}>
                                <td className="px-4 py-2 text-gray-700">{a.periodMonth}</td>
                                <td className="px-4 py-2 font-medium text-green-700">{formatCents(a.commissionCents)}</td>
                                <td className="px-4 py-2 text-gray-500 text-xs">{a.statementId ? `Statement #${a.statementId}` : "Pending"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tier === "regular_host" && earnings.activeCreditCents > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Your bill credits</h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Gift className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-800">Active credit balance: {formatCents(earnings.activeCreditCents)}</p>
                        <p className="text-xs text-green-700 mt-0.5">Credits are automatically applied to your next invoice.</p>
                      </div>
                    </div>
                  </div>
                  {earnings.credits.filter(c => !c.redeemedAt).length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="table-credits">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left px-4 py-2 font-medium text-gray-500">Credit</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500">Amount</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500">Expires</th>
                          </tr>
                        </thead>
                        <tbody>
                          {earnings.credits.filter(c => !c.redeemedAt).map((c) => (
                            <tr key={c.id} className="border-b last:border-0" data-testid={`row-credit-${c.id}`}>
                              <td className="px-4 py-2 text-gray-700 capitalize">{c.creditType.replace(/_/g, " ")}</td>
                              <td className="px-4 py-2 font-medium text-green-700">{formatCents(c.amountCents)}</td>
                              <td className="px-4 py-2 text-gray-500 text-xs">{format(new Date(c.expiresAt), "MMM d, yyyy")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {earnings.statements.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Quarterly statements</h3>
                  <div className="space-y-2">
                    {earnings.statements.map((s) => (
                      <div key={s.id} className="border rounded-lg p-4 flex items-center justify-between" data-testid={`card-statement-${s.id}`}>
                        <div className="flex items-center gap-3">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{s.periodQuarter}</p>
                            <p className="text-xs text-gray-500">{s.paidAt ? `Paid ${format(new Date(s.paidAt), "MMM d, yyyy")}` : "Pending payment"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-bold text-gray-900">{formatCents(s.totalCents)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statementStatusColors[s.status] || "bg-gray-100 text-gray-600"}`}>
                            {s.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Referral status</h3>
                {tier === "regular_host" && (
                  <div className="bg-gray-100 rounded-lg px-5 py-4 mb-4">
                    <p className="text-sm text-gray-600">You earn a $25 credit when your referral becomes a paying Growth or Professional member. They get a $25 credit too.</p>
                  </div>
                )}

                {referralList.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid="table-referrals">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Email</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Date Sent</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referralList.map((ref) => (
                          <tr key={ref.id} className="border-b last:border-0" data-testid={`row-referral-${ref.id}`}>
                            <td className="px-4 py-3 text-gray-900">{ref.referredEmail}</td>
                            <td className="px-4 py-3 text-gray-600">{format(new Date(ref.createdAt), "MMM d, yyyy")}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[ref.status] || "bg-gray-100 text-gray-700"}`}>
                                {statusLabels[ref.status] || ref.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No referrals sent yet. Share your link or invite friends by email above.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
